import React, { useCallback, useMemo } from 'react';
import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Avatar, Box, Stack, Typography } from '@lyearn/ui';
import { Image } from '@/components/Image';
import { Link } from '@/components/Link';
import RichTextViewer from '@/components/RichTextViewer';
import { NotificationActionTypeEnum, UserNotificationStatusEnum } from '@/types/schema';

import { NotificationDetailsFragment } from '../NotificationTray/graphql/fragments/generated/NotificationDetails';

interface NotificationItemProps {
  index: number;
  userNotification: NotificationDetailsFragment;
  onNotificationItemClick: (userNotification: NotificationDetailsFragment) => void;
  onClickBadgeType: (userNotification: NotificationDetailsFragment) => void;
  onClickPointsAwarded: (userNotification: NotificationDetailsFragment) => void;
}

// for native
const tagStyles = { b: 'font-normal text-text-primary' };

const CONTAINER_CLASSNAME = 'flex-1 rounded';

const USER_TYPE_NOTIFICATION = [
  NotificationActionTypeEnum.CommunityChannelMentioned,
  NotificationActionTypeEnum.UserMasquerading,
  NotificationActionTypeEnum.OkrCheckin,
  NotificationActionTypeEnum.OkrsUpdated,
  NotificationActionTypeEnum.OkrDeleted,
  NotificationActionTypeEnum.OneOnOneReminder,
  NotificationActionTypeEnum.OneOnOneSetup,
  NotificationActionTypeEnum.OneOnOneScheduleUpdate,
  NotificationActionTypeEnum.PraiseGiven,
  NotificationActionTypeEnum.PeerFeedbackRequested,
  NotificationActionTypeEnum.PeerFeedbackGiven,
  NotificationActionTypeEnum.UserMention,
  NotificationActionTypeEnum.OneOnOneMissed,
  NotificationActionTypeEnum.OneOnOneDeactivated,
  NotificationActionTypeEnum.OneOnOneTemplateSetup,
  NotificationActionTypeEnum.OneOnOneTemplateUpdated,
  NotificationActionTypeEnum.OneOnOneInstanceRescheduled,
  NotificationActionTypeEnum.OneOnOneStart,
];

const EXCLUDE_REDIRECTION = [NotificationActionTypeEnum.UnAssignContent];
const NotificationItem: React.FC<NotificationItemProps> = (props) => {
  const { t } = useTranslation();
  const { isMobile } = useScreen();
  const { userNotification, onNotificationItemClick, onClickBadgeType, onClickPointsAwarded } =
    props;

  const onClick = React.useCallback(() => {
    onNotificationItemClick?.(userNotification);
  }, [onNotificationItemClick, userNotification]);

  const redirectionEnabled = useMemo(
    () => !!userNotification.redirectPath && !EXCLUDE_REDIRECTION.includes(userNotification.type),
    [userNotification.redirectPath, userNotification.type],
  );

  const _onClick = useCallback(() => {
    if (userNotification.type === NotificationActionTypeEnum.BadgeUnlocked) {
      onClickBadgeType(userNotification);
    }
    if (userNotification.type === NotificationActionTypeEnum.PointsAwarded) {
      onClickPointsAwarded(userNotification);
    }
  }, [onClickBadgeType, onClickPointsAwarded, userNotification]);

  const renderContent = () => (
    <Stack
      className={cn('mx-8 mb-4 py-16 xs:mx-0 xs:py-12 cursor-pointer')}
      direction="row"
      onClick={_onClick}>
      {USER_TYPE_NOTIFICATION.includes(userNotification.type) ? (
        <Avatar className="flex-shrink-0 self-start" size="xx-large" src={userNotification.icon!} />
      ) : (
        <Image
          alt={'notification-preview'}
          className="h-48 w-48 flex-shrink-0 self-start rounded-8"
          height={48}
          objectFit="contain"
          src={userNotification.icon!}
          width={48}
        />
      )}
      <Stack className="flex flex-1 ms-12" direction="col">
        <Stack direction="row" justifyContent="between">
          <RichTextViewer
            className="body-short-02 mb-8 flex-shrink"
            color="text-text-secondary"
            html={userNotification.message}
            // for native
            tagStyles={tagStyles}
            variant={isMobile ? 'label-01' : 'body-short-02'}
          />
          <Stack
            className={cn(
              'mt-8 h-8 w-8 flex-shrink-0 rounded-full mx-16',
              userNotification.status === UserNotificationStatusEnum.Unread
                ? 'bg-icon-error'
                : 'bg-transparent',
            )}
          />
        </Stack>
        {userNotification.quotedMessage ? (
          <Box className="mb-8 rounded border border-border px-12 py-8">
            <Typography color="text-text-paragraph" variant="body-short-02">
              {userNotification.quotedMessage}
            </Typography>
          </Box>
        ) : null}
        <Typography color="text-text-secondary" variant="body-short-03">
          {t('Common:Notifications.CreatedAt', {
            createdAt: formatDistanceToNowStrict(new Date(userNotification.createdAt)),
          })}
        </Typography>
      </Stack>
    </Stack>
  );

  if (redirectionEnabled) {
    return (
      <Link
        showHighlight
        className={CONTAINER_CLASSNAME}
        to={userNotification.redirectPath}
        onClick={onClick}>
        {renderContent()}
      </Link>
    );
  }

  return (
    <Box className={CONTAINER_CLASSNAME} onClick={onClick}>
      {renderContent()}
    </Box>
  );
};

export default React.memo(NotificationItem);
